import html2pdf from 'html2pdf.js';
import React from 'react';
import { ClearFilterButtonProps } from './interfaces/Ibuttons';
import { ViewAnalyticsButtonProps } from './interfaces/Ibuttons';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { PositionIcon, EditIcon, ClearIcon, CancelIcon, BarChartIcon } from '../utils/IconSet';

// Generic button for editing a row (role, user, etc.)
export function EditButton({
    onClick,
    title = 'Edit',
    className,
}: {
    onClick: () => void;
    title?: string;
    className?: string;
}) {
    return (
        <button onClick={onClick} title={title} className={className}>
            <EditIcon className='icon-blue' />
        </button>
    );
}

// Button to open Telegram with logo and light blue color
export const TelegramButton: React.FC<{ url?: string; title?: string; className?: string }> = ({
    url = 'https://t.me/',
    title = 'Open Telegram',
    className = '',
}) => {
    const handleClick = async () => {
        try {
            await fetch('/api/send_message', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ message: 'Clicked' }),
            });
        } catch (err) {
            // Optionally handle error
        }
    };
    return (
        <button
            onClick={() => {
                handleClick();
            }}
            title={title}
            className={className}
            style={{
                background: '#229ED9',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 10px',
                display: 'flex',
                alignItems: 'center',
                color: '#fff',
                cursor: 'pointer',
            }}
        >
            {/* Telegram SVG Logo */}
            <svg
                width='22'
                height='22'
                viewBox='0 0 240 240'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                style={{ marginRight: 6 }}
            >
                <circle cx='120' cy='120' r='120' fill='#229ED9' />
                <path
                    d='M180.5 72.5L157.5 180.5C155.5 188.5 150.5 190.5 143.5 186.5L110.5 161.5L94.5 176.5C92.5 178.5 90.5 180.5 87.5 180.5L90.5 146.5L157.5 85.5C160.5 82.5 157.5 81.5 153.5 84.5L75.5 137.5L42.5 127.5C35.5 125.5 35.5 120.5 44.5 117.5L170.5 71.5C177.5 69.5 182.5 73.5 180.5 72.5Z'
                    fill='#fff'
                />
            </svg>
            Telegram
        </button>
    );
};

// Generic button for deleting a row (role, user, etc.)
export function DeleteButton({
    onClick,
    title = 'Delete',
    className,
}: {
    onClick: () => void;
    title?: string;
    className?: string;
}) {
    return (
        <button onClick={onClick} title={title} className={className}>
            <CancelIcon className='icon-red' />
        </button>
    );
}

// Button for removing a proxy from an asset row
export function RemoveProxyButton({ row, onClick, title }: { row: any; onClick: () => void; title?: string }) {
    return (
        <button onClick={onClick} title={title || 'Remove Proxy'}>
            <ClearIcon className='icon-red' />
        </button>
    );
}

// Button for showing asset positions
export function AssetPositionButton({ onClick, title }: { onClick: () => void; title?: string }) {
    return (
        <button type='button' title={title || 'Show Positions'} onClick={onClick}>
            <PositionIcon className='icon-green' />
        </button>
    );
}

// Button for viewing account positions

export function AccountPositionButton({
    account,
    onClick,
    title,
}: {
    account: any;
    onClick: () => void;
    title?: string;
}) {
    return (
        <button onClick={onClick} title={title || 'View Positions'}>
            <PositionIcon className='icon-green' />
        </button>
    );
}

// Async function to export table as PDF with logos
export async function exportTableAsPDF(tableId?: string) {
    const table = tableId ? document.getElementById(tableId) : document.querySelector('.elegant-table');
    if (table && html2pdf) {
        const logoUrl = require('../../assets/G-20_Logo_Gradient.png');
        const opt = {
            margin: [5, 5, 5, 5] as [number, number, number, number],
            filename: 'BalanceSheet.pdf',
            image: { type: 'jpeg' as 'jpeg', quality: 1 },
            html2canvas: { scale: 4, dpi: 300, useCORS: true },
            jsPDF: { unit: 'mm', format: 'a3', orientation: 'landscape' as 'landscape' },
        };
        const worker: any = html2pdf()
            .set(opt)
            .from(table as HTMLElement)
            .toPdf();

        const pdf: any = await worker.get('pdf');
        // Get page dimensions
        const pageHeight = pdf.internal.pageSize.getHeight();
        // Load logo
        const img = new Image();
        img.src = logoUrl;
        await new Promise((resolve) => {
            img.onload = resolve;
        });
        // Set logo size
        const logoWidth = 40;
        const logoHeight = (img.height / img.width) * logoWidth;
        // Draw logo at bottom left
        pdf.addImage(
            img,
            'PNG',
            15, // left margin
            pageHeight - logoHeight - 10, // 10mm from bottom
            logoWidth,
            logoHeight
        );

        await worker.save();
    } else {
        alert('Could not find balance table or html2pdf library.');
    }
}

// Function to export table as CSV
export function exportTableAsCSV(tableId?: string) {
    const table = tableId ? document.getElementById(tableId) : document.querySelector('.elegant-table');
    if (!table) {
        alert('Could not find balance table.');
        return;
    }
    let csv = '';
    // Get header row
    const rows = table.querySelectorAll('tr');
    rows.forEach((row) => {
        const cells = row.querySelectorAll('th,td');
        const rowData: string[] = [];
        cells.forEach((cell) => {
            // Remove commas and newlines from cell text
            const text = cell.textContent || '';
            rowData.push('"' + text.replace(/\n/g, ' ').replace(/"/g, '""') + '"');
        });
        csv += rowData.join(',') + '\n';
    });
    // Download CSV
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'BalanceSheet.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

// Expand/collapse row button for tables
export function ExpandRowButton({
    expanded,
    onClick,
    title,
}: {
    expanded: boolean;
    onClick: () => void;
    title?: string;
}) {
    return (
        <button
            onClick={onClick}
            title={title || (expanded ? 'Hide details' : 'Show details')}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </button>
    );
}

export const ViewAnalyticsButton: React.FC<ViewAnalyticsButtonProps> = ({
    account,
    onTabClick,
    className = '',
    title = 'View Analytics',
}) => (
    <button
        onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            const tabType = 'accountanalytics';
            const value = account;
            if (e.ctrlKey || e.metaKey) {
                const url = `${window.location.origin}/#${tabType}/${encodeURIComponent(account.location_id)}`;
                window.open(url, '_blank');
            } else {
                window.location.hash = `#${tabType}/${encodeURIComponent(account.location_id)}`;
                onTabClick(tabType, value);
            }
        }}
        title={title}
        className={className}
    >
        <BarChartIcon className='icon-red' />
    </button>
);

export const ClearFilterButton: React.FC<ClearFilterButtonProps> = ({
    onClick,
    title = 'Clear filter',
    className = 'filter-button',
}) => (
    <button onClick={onClick} title={title} className={className}>
        <ClearIcon className='icon-red' />
    </button>
);

export const PrintPDFButton: React.FC<{ tableId?: string }> = ({ tableId }) => (
    <button className='adobe-print-pdf-btn' onClick={() => exportTableAsPDF(tableId)}>
        {/* Adobe logo SVG */}
        <svg
            width='22'
            height='22'
            viewBox='0 0 32 32'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className='adobe-print-pdf-logo'
        >
            <rect width='32' height='32' rx='6' fill='#fff' />
            <path d='M7.5 24L16 8L24.5 24H21.5L16 13.5L10.5 24H7.5Z' fill='#FF0000' />
        </svg>
        PDF
    </button>
);

export const DownloadCSVButton: React.FC<{ tableId?: string }> = ({ tableId }) => (
    <button className='download-csv-btn' style={{ marginLeft: '8px' }} onClick={() => exportTableAsCSV(tableId)}>
        {/* CSV icon SVG */}
        <svg
            width='22'
            height='22'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className='download-csv-logo'
        >
            <rect x='2' y='2' width='20' height='20' rx='4' fill='#fff' />
            <path d='M7 17V7H9V17H7ZM11 17V7H13V17H11ZM15 17V7H17V17H15Z' fill='#2ECC40' />
        </svg>
        CSV
    </button>
);
